/**
 * 团队管理器
 * 默认团队与工作区是可用的
 */
import { useEffect, useState } from "react";
import { useMatch } from "react-router-dom";
import { usersTeams } from "@/services/users-teams";
import { workspaceTeam } from "@/services/workspace-team";
import { updateWorkspace } from "@/services/update-workspace";
import { deleteWorkspace } from "@/services/delete-workspace";
import { workspace } from "@/services/workspace";
import { useToast } from "@/hooks/use-toast";
import { useI18nNavigate } from "@/hooks/use-i18n-navigate";
import { iamAccountManager } from "./iam-account-manager";
import { routeLastVisitCreationManager } from "./route-last-visit-creation-manager";

type TeamItem = {
  id: number;
  teamName: string;
  teamCode: string;
  masterUserId: number;
  memberCount: number;
  workspaceCount: number;
  isCurrentUserMaster: boolean;
};

type WorkspaceItem = {
  id: string;
  name: string;
  role: string;
  memberCount: number;
};

export class IamTeamManager {
  private static instance: IamTeamManager;
  // 团队与工作区事件键
  private readonly TEAM_EVENT_KEY = "MIUTA_TEAM_CHANGE";
  private readonly WORKSPACE_EVENT_KEY = "MIUTA_WORKSPACE_CHANGE";
  // 团队与工作区列表缓存、存储键与事件键
  private readonly TEAM_LIST_STORAGE_KEY = "MIUTA_TEAM_LIST";
  private readonly WORKSPACE_MAP_STORAGE_KEY = "MIUTA_WORKSPACE_MAP";
  private readonly TEAM_LIST_EVENT_KEY = "MIUTA_TEAM_LIST_CHANGE";
  private readonly WORKSPACE_LIST_EVENT_KEY = "MIUTA_WORKSPACE_LIST_CHANGE";
  // 团队与工作区列表内存缓存
  private teamList: TeamItem[] = [];
  private workspaceMap: Record<string, WorkspaceItem[]> = {};

  private constructor() {}

  public static getInstance(): IamTeamManager {
    if (!IamTeamManager.instance) {
      IamTeamManager.instance = new IamTeamManager();
    }
    return IamTeamManager.instance;
  }

  // 写入团队列表缓存
  private saveTeamListCache(): void {
    try {
      localStorage.setItem(
        this.TEAM_LIST_STORAGE_KEY,
        JSON.stringify({ teams: this.teamList, timestamp: Date.now() })
      );
    } catch {
      // ignore save error
    }
  }

  // 写入工作区列表缓存
  private saveWorkspaceMapCache(): void {
    try {
      localStorage.setItem(
        this.WORKSPACE_MAP_STORAGE_KEY,
        JSON.stringify(this.workspaceMap)
      );
    } catch {
      // ignore save error
    }
  }

  // 获取团队序列和当前团队工作区序列
  public async init(): Promise<void> {
    try {
      this.changeTeam(iamAccountManager.get().teamCode);
      // 获取团队列表
      const teamRes = await usersTeams();
      this.teamList = teamRes.teams || [];
      this.saveTeamListCache();
      this.dispatchTeamListChange();
    } catch (e) {
      // 保持失败不抛出，避免阻断应用
      console.error("IamTeamManager init failed:", e);
    }
  }

  public async addWorkspace({
    name,
  }: { name: string }): Promise<void> {
    const teamCode = iamAccountManager.get().teamCode;
    if (!teamCode) {
      // 无团队无法添加到列表，直接退出
      return;
    }
    try {
      // 创建新工作区
      const res = await workspace({ name });
      // 切换当前工作区
      this.changeWorkspace(res.workspace.id);
      // 获取当前团队的工作区列表
      this.fetchWorkspaceByTeam(true);
    } catch (error) {
      console.log(error)
      throw error;
    }
  }

  public async updateWorkspaceInfo({
    id,
    name,
    description,
  }: { id: string; name: string; description: string }): Promise<void> {
    const teamCode = iamAccountManager.get().teamCode;
    // 无团队无法添加到列表，直接退出
    if (!teamCode) {
      return;
    }
    const list = this.workspaceMap[teamCode] || [];
    const index = list.findIndex((item) => item.id === id);
    // 工作区不存在，直接退出
    if (index === -1) {
      return;
    } 

    const oldName = list[index].name;

    // 乐观更新
    this.workspaceMap[teamCode] = list.map((item) =>
      item.id === id ? { ...item, name } : item
    );
    this.saveWorkspaceMapCache();
    this.dispatchWorkspaceListChange(teamCode);

    try {
      await updateWorkspace({ id, name, description });
      // 后台刷新
      await this.fetchWorkspaceByTeam();
    } catch (error) {
      // 回滚
      this.workspaceMap[teamCode] = list.map((item) =>
        item.id === id ? { ...item, name: oldName } : item
      );
      this.saveWorkspaceMapCache();
      this.dispatchWorkspaceListChange(teamCode);
      throw error;
    }
  }

  public async deleteWorkspaceById(id: string): Promise<void> {
    const teamCode = iamAccountManager.get().teamCode;
    // 无团队无法删除，直接退出
    if (!teamCode) {
      return;
    }
    const list = this.workspaceMap[teamCode] || [];
    const target = list.find((item) => item.id === id);
    // 工作区不存在，直接退出
    if (!target) {
      return;
    }
    await deleteWorkspace(id);
    this.workspaceMap[teamCode] = list.filter((item) => item.id !== id);
    this.saveWorkspaceMapCache();
    this.dispatchWorkspaceListChange(teamCode);
    // 如果删除的是当前选中的工作区，切换到第一个
    if (id === iamAccountManager.get().latestModifiedWorkspace.id) {
      const workspaceId = this.workspaceMap[teamCode][0]?.id || "";
      this.changeWorkspace(workspaceId);
    }
    // 后台刷新
    this.fetchWorkspaceByTeam();
  }

  // 按团队获取工作区列表（缓存+事件）
  public async fetchWorkspaceByTeam(refresh = false): Promise<void> {
    const teamCode = iamAccountManager.get().teamCode;
    if (!teamCode) {
      return;
    }
    try {
      // 如果已缓存，直接派发事件
      if (!refresh && Array.isArray(this.workspaceMap[teamCode]) && this.workspaceMap[teamCode].length > 0) {
        this.dispatchWorkspaceChange();
        // 后台同步更新
        this.fetchWorkspaceByTeam(true);
        return;
      }
      const wsRes = await workspaceTeam({
        teamCode,
      });
      const workspaces = wsRes.response?.workspaces || [];
      this.workspaceMap[teamCode] = workspaces;
      this.saveWorkspaceMapCache();
      this.dispatchWorkspaceListChange(teamCode);
    } catch (e) {
      console.error("Fetch workspace list failed:", e);
    }
  }

  // 团队变更事件派发
  private dispatchTeamChange(): void {
    const event = new CustomEvent<{ teamCode: string }>(this.TEAM_EVENT_KEY, { detail: { teamCode: iamAccountManager.get().teamCode } });
    window.dispatchEvent(event);
  }

  // 工作区变更事件派发
  private dispatchWorkspaceChange(): void {
    const event = new CustomEvent<{ workspaceId: string }>(this.WORKSPACE_EVENT_KEY, { detail: { workspaceId: iamAccountManager.get().latestModifiedWorkspace.id } });
    window.dispatchEvent(event);
  }

  // 团队列表事件派发
  private dispatchTeamListChange(): void {
    const event = new CustomEvent<{ teams: TeamItem[] }>(this.TEAM_LIST_EVENT_KEY, { detail: { teams: this.teamList } });
    window.dispatchEvent(event);
  }

  // 工作区列表事件派发
  private dispatchWorkspaceListChange(teamCode: string): void {
    const workspaces = this.workspaceMap[teamCode] || [];
    const event = new CustomEvent<{ teamCode: string; workspaces: WorkspaceItem[] }>(
      this.WORKSPACE_LIST_EVENT_KEY,
      { detail: { teamCode, workspaces } }
    );
    window.dispatchEvent(event);
  }

  public getTeamCode(): string {
    return iamAccountManager.get()?.teamCode;
  }

  public getWorkspaceId(): string {
    return iamAccountManager.get().latestModifiedWorkspace.id;
  }

  // 更新当前团队
  public setTeamCode(teamCode: string): void {
    iamAccountManager.update({
      ...iamAccountManager.get(),
      teamCode,
    });
    this.dispatchTeamChange();
  }

  // 更新当前工作区
  public setWorkspaceId(workspaceId: string): void {
    iamAccountManager.update({
      ...iamAccountManager.get(),
      latestModifiedWorkspace: {
        id: workspaceId,
      },
    });
    this.dispatchWorkspaceChange();
  }

  // 切换团队
  public changeTeam(teamCode: string): void {
    this.setTeamCode(teamCode);
  }

  // 切换当前工作区
  public changeWorkspace(workspaceId: string): void {
    this.setWorkspaceId(workspaceId);
  }

  // 获取团队序列
  public getTeamList(): TeamItem[] {
    return this.teamList;
  }

  // 获取当前/指定团队的工作区序列
  public getWorkspaceList(teamCode?: string): WorkspaceItem[] {
    const code = teamCode || iamAccountManager.get()?.teamCode;
    return this.workspaceMap[code] || [];
  }

  // 获取当前团队信息
  public getCurrentTeam(): TeamItem | undefined {
    return this.teamList.find(t => t.teamCode === iamAccountManager.get().teamCode);
  }

  // 获取当前工作区信息
  public getCurrentWorkspace(): WorkspaceItem | undefined {
    const list = this.getWorkspaceList();
    return list.find(ws => ws.id === iamAccountManager.get().latestModifiedWorkspace.id);
  }

  // 清空
  public clear(): void {
    iamAccountManager.update({
      ...iamAccountManager.get(),
      teamCode: "",
      latestModifiedWorkspace: {
        id: "",
      },
    });
    this.dispatchTeamChange();
    this.dispatchWorkspaceChange();
    // 同步清空缓存
    this.teamList = [];
    this.workspaceMap = {};
    this.saveTeamListCache();
    this.saveWorkspaceMapCache();
    this.dispatchTeamListChange();
    this.dispatchWorkspaceListChange(iamAccountManager.get().teamCode);
  }

  // 订阅团队变化
  public onTeamChange(listener: (e: CustomEvent<{ teamCode: string }>) => void) {
    window.addEventListener(this.TEAM_EVENT_KEY, listener);
    return () => window.removeEventListener(this.TEAM_EVENT_KEY, listener);
  }

  // 订阅工作区变化
  public onWorkspaceChange(listener: (e: CustomEvent<{ workspaceId: string }>) => void) {
    window.addEventListener(this.WORKSPACE_EVENT_KEY, listener);
    return () => window.removeEventListener(this.WORKSPACE_EVENT_KEY, listener);
  }

  // 订阅团队列表变化
  public onTeamListChange(listener: (e: CustomEvent<{ teams: TeamItem[] }>) => void) {
    window.addEventListener(this.TEAM_LIST_EVENT_KEY, listener);
    return () => window.removeEventListener(this.TEAM_LIST_EVENT_KEY, listener);
  }

  // 订阅工作区列表变化
  public onWorkspaceListChange(listener: (e: CustomEvent<{ teamCode: string; workspaces: WorkspaceItem[] }>) => void) {
    window.addEventListener(this.WORKSPACE_LIST_EVENT_KEY, listener);
    return () => window.removeEventListener(this.WORKSPACE_LIST_EVENT_KEY, listener);
  }
}

export const iamTeamManager = IamTeamManager.getInstance();

export const iamTeamHooks = {
  // useTeamList 返回团队列表，并监听变化
  useTeamList: () => {
    const [loading, setLoading] = useState(false);
    const [teams, setTeams] = useState<TeamItem[]>(iamTeamManager.getTeamList());

    useEffect(() => {
      // 初始化加载
      setLoading(true);
      iamTeamManager.init().finally(() => {
        setLoading(false);
      });
      return iamTeamManager.onTeamListChange((e) => {
        setTeams(e.detail.teams);
      });
    }, []);

    return { loading, teams };
  },

  // useWorkspaceList 返回当前团队的工作区列表，并监听变化
  useWorkspaceList: () => {
    const [loading, setLoading] = useState(false);
    const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>(iamTeamManager.getWorkspaceList());

    useEffect(() => {
      // 初始化加载
      setLoading(true);
      // 若当前团队已选中，拉取其工作区列表
      const code = iamTeamManager.getTeamCode();
      if (code) {
        iamTeamManager.fetchWorkspaceByTeam(true).finally(() => {
          setLoading(false);
        });
      }
      // 工作区列表变化监听
      const offWs = iamTeamManager.onWorkspaceListChange((e) => {
        // 只更新当前选中团队对应的工作区列表
        if (e.detail.teamCode === iamTeamManager.getTeamCode()) {
          setWorkspaces(e.detail.workspaces);
        }
      });
      return () => {
        offWs();
        // offTeam();
      };
    }, []);

    return { loading, workspaces };
  },

  // useChangeTeam 切换团队钩子
  useChangeTeam: () => {
    const navigate = useI18nNavigate();
    const { showToastByCode } = useToast();

    const changeTeam = (teamCode: string) => {
      try {
        iamTeamManager.changeTeam(teamCode);
        navigate("/")
      } catch (error) {
        showToastByCode(error);
      }
    };

    return { changeTeam };
  },

  // useChangeWorkspace 切换工作区钩子
  useChangeWorkspace: () => {
    const navigate = useI18nNavigate();
    const isFolder = useMatch("/creation/folder/:id/");
    const isProject = useMatch("/creation/project/:id/");

    const changeWorkspace = async (workspaceId: string) => {
      iamTeamManager.changeWorkspace(workspaceId);
      // 只有在创作模块需要刷新内容
      if (isFolder || isProject) {
        navigate(await routeLastVisitCreationManager.get(), { replace: true });
      }
    };
    return { changeWorkspace };
  },

  // useCurrentTeam 获取当前团队信息
  useCurrentTeam: () => {
    const [currentTeam, setCurrentTeam] = useState<TeamItem | undefined>(iamTeamManager.getCurrentTeam());

    useEffect(() => {
      const off = iamTeamManager.onTeamChange(() => {
        setCurrentTeam(iamTeamManager.getCurrentTeam());
      });
      return () => {
        off();
      };
    }, []);

    return { currentTeam };
  },

  // useCurrentWorkspace 获取当前工作区信息
  useCurrentWorkspace: () => {
    const [currentWorkspace, setCurrentWorkspace] = useState<WorkspaceItem | undefined>(iamTeamManager.getCurrentWorkspace());

    useEffect(() => {
      const off = iamTeamManager.onWorkspaceChange(() => {
        setCurrentWorkspace(iamTeamManager.getCurrentWorkspace());
      });
      return () => {
        off();
      };
    }, []);

    return { currentWorkspace };
  },

  // useRefreshWorkspace 刷新工作区钩子
  useRefreshWorkspace: () => {
    const { showToastByCode } = useToast();

    const refreshWorkspaceList = async () => {
      try {
        await iamTeamManager.fetchWorkspaceByTeam();
      } catch (error) {
        showToastByCode(error);
      }
    };

    return { refreshWorkspaceList };
  },

  // useAddWorkspace 添加工作区钩子
  useAddWorkspace: () => {
    const navigate = useI18nNavigate();
    const isFolder = useMatch("/creation/folder/:id/");
    const isProject = useMatch("/creation/project/:id/");
    const { showToastByCode } = useToast();

    const addWorkspace = async ({ name }: { name: string }) => {
      try {
        await iamTeamManager.addWorkspace({ name });
        // 只有在创作模块需要刷新内容
        if (isFolder || isProject) {
          navigate(await routeLastVisitCreationManager.get(), { replace: true });
        } 
      } catch (error) {
        showToastByCode(error);
      }
    };

    return { addWorkspace };
  },

  // useUpdateWorkspace 更新工作区钩子
  useUpdateWorkspace: () => {
    const { showToastByCode } = useToast();

    const updateWorkspace = async ({
      id,
      name,
      description,
    }: {
      id: string;
      name?: string;
      description?: string;
    }) => {
      try {
        await iamTeamManager.updateWorkspaceInfo({ id, name, description });
      } catch (error) {
        showToastByCode(error);
      }
    };

    return { updateWorkspace };
  },

  // useDeleteWorkspace 删除工作区钩子
  useDeleteWorkspace: () => {
    const navigate = useI18nNavigate();
    const isFolder = useMatch("/creation/folder/:id/");
    const isProject = useMatch("/creation/project/:id/");
    const { showToastByCode } = useToast();

    const deleteWorkspace = async (id: string) => {
      try {
        const workspaceId = iamTeamManager.getWorkspaceId();
        await iamTeamManager.deleteWorkspaceById(id);
        // 删除当前工作区时 会切换到第一个工作区 需刷新创作模块
        if (id === workspaceId) {
          // 只有在创作模块需要刷新内容
          if (isFolder || isProject) {
            navigate(await routeLastVisitCreationManager.get(), { replace: true });
          }
        }
      } catch (error) {
        showToastByCode(error);
      }
    };

    return { deleteWorkspace };
  },
};
